import { useState, useEffect } from "react";
import axios from "axios";
import Header from "../Components/Header";
import { BRAND } from "../css/brand";
import { RefreshCw, RotateCcw, Download } from "lucide-react";
import BlueButton from "../Components/BlueButton";
import GoBackButton from "../Components/GoBackButton";
import TableSkeleton from "../Components/TableSkeleton";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

export default function DeletedCustomers() {
  const [customers, setCustomers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = authUserString ? JSON.parse(authUserString) : null;
  const loginId = authUser?.data?.[0]?.id || 26;

  // ✅ Fetch deleted customers
  const fetchDeletedCustomers = async (page = 1, limit = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `https://spillas.com/api/v1/get-deleted-customers?page=${page}&per_page=${limit}&login_id=${loginId}&year=2025`
      );
      const data = res.data.data.data || [];
      setCustomers(data);
      setTotalPages(res.data.data.last_page || 1);
      setSelectedIds([]);
    } catch (err) {
      console.error("Error fetching deleted customers:", err);
      setError("Failed to load deleted customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedCustomers(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  // ✅ Select logic
  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === customers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(customers.map((c) => c.id));
    }
  };

  // ✅ Restore customers
  const handleRestore = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Customers Selected",
        text: "Please select at least one customer to recover.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Recover selected customers?",
      text: `You are about to recover ${selectedIds.length} customer(s).`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Recover",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        "https://spillas.com/api/v1/restore-customers",
        { ids: selectedIds, login_id: loginId }
      );

      if (res.data?.status === true) {
        await Swal.fire({
          icon: "success",
          title: "Recovered!",
          text: "Customers recovered successfully.",
          confirmButtonColor: "#2563eb",
        });
        fetchDeletedCustomers(currentPage, rowsPerPage);
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Failed to recover customers.",
          confirmButtonColor: "#2563eb",
        });
      }
    } catch (err) {
      console.error("Error restoring customers:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while recovering customers.",
        confirmButtonColor: "#2563eb",
      });
    }
  };

  // ✅ Download CSV (All Pages)
  const handleDownload = async () => {
    try {
      setLoading(true);
      let allData = [];
      let currentPage = 1;
      const perPage = 200;
      let totalPages = 1;

      do {
        const res = await axios.get(
          `https://spillas.com/api/v1/get-deleted-customers?page=${currentPage}&per_page=${perPage}&login_id=${loginId}&year=2025`
        );
        const data = res.data?.data?.data || [];
        if (data.length === 0) break;
        allData = [...allData, ...data];
        totalPages = res.data?.data?.last_page || 1;
        currentPage++;
      } while (currentPage <= totalPages);

      if (allData.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "No deleted customers found to download.",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      const headers = [
        "Sr No.",
        "Customer Name",
        "IMEI",
        "Retailer Code",
        "Shop Name",
        "Registration Date",
        "Deleted Date",
      ];

      const csvRows = [
        headers.join(","),
        ...allData.map((item, i) =>
          [
            i + 1,
            `"${item.name || "-"}"`,
            `"${item.imei1 || "-"}"`,
            `"${item.retailer_code || "-"}"`,
            `"${item.created_by_name || "-"}"`,
            item.created_at
              ? new Date(item.created_at).toLocaleDateString()
              : "-",
            item.updated_at
              ? new Date(item.updated_at).toLocaleDateString()
              : "-",
          ].join(",")
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "deleted_customers.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Download Complete",
        text: "Deleted customers exported successfully.",
        confirmButtonColor: "#2563eb",
      });
    } catch (error) {
      console.error("❌ Download failed:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to download data.",
        confirmButtonColor: "#2563eb",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <GoBackButton />
      <Header brand={BRAND} title="Deleted Customers" />

      {/* Top Actions */}
      <div className="flex flex-wrap justify-end items-center gap-2 sm:gap-3">
        {/* Rows per page (desktop) */}
        <div className="hidden sm:flex items-center gap-2 text-sm">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>

        {/* Mobile rows selector */}
        <div className="flex sm:hidden items-center">
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* Refresh */}
          <BlueButton
            brand={BRAND}
            onClick={() => fetchDeletedCustomers(currentPage, rowsPerPage)}
            className="inline-flex items-center gap-1 sm:px-3 sm:py-2 px-2 py-1"
          >
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Refresh</span>
          </BlueButton>

          {/* Recover */}
          <BlueButton
            brand={BRAND}
            onClick={handleRestore}
            className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 sm:px-3 sm:py-2 px-2 py-1"
          >
            <RotateCcw size={16} />
            <span className="hidden sm:inline">Recover</span>
          </BlueButton>

          {/* Download */}
          <BlueButton
            brand={BRAND}
            onClick={handleDownload}
            className="inline-flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 sm:px-3 sm:py-2 px-2 py-1"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Download</span>
          </BlueButton>


        </div>
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : customers.length === 0 ? (
        <p>No deleted customers found.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
          <table className="min-w-full text-sm text-center">
            <thead className="bg-slate-50 text-center">
              <tr className="text-left text-[11px] sm:text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 bg-slate-50 text-center">
                  <input
                    type="checkbox"
                    checked={
                      customers.length > 0 &&
                      selectedIds.length === customers.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Sr No.</th>
                <th>Customer Name</th>
                <th>IMEI</th>
                <th>Retailer Code</th>
                <th>Shop Name</th>
                <th>Registration</th>
                <th>Deleted</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((item, index) => (
                <tr
                  key={item.id}
                  className={`border-t hover:bg-slate-50 ${
                    selectedIds.includes(item.id) ? "bg-blue-50" : ""
                  }`}
                >
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleSelect(item.id)}
                    />
                  </td>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td>{item.name || "-"}</td>
                  <td>{item.imei1 || "-"}</td>
                  <td>{item.retailer_code || "-"}</td>
                  <td>{item.created_by_name || "-"}</td>
                  <td>
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    {item.updated_at
                      ? new Date(item.updated_at).toLocaleDateString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-end items-center mt-4 gap-3">
        <div className="flex gap-2 items-center">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            className={`px-2 py-1 border rounded text-sm ${
              currentPage === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-slate-100"
            }`}
          >
            Prev
          </button>

          {/* Page Numbers */}
          {(() => {
            const pages = [];
            const maxVisible = 5;
            let start = Math.max(1, currentPage - 2);
            let end = Math.min(totalPages, start + maxVisible - 1);
            if (end - start < maxVisible - 1)
              start = Math.max(1, end - maxVisible + 1);
            if (start > 1) {
              pages.push(1);
              if (start > 2) pages.push("...");
            }
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages) {
              if (end < totalPages - 1) pages.push("...");
              pages.push(totalPages);
            }
            return pages.map((p, idx) =>
              p === "..." ? (
                <span key={idx} className="px-2 text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-2 py-1 border rounded text-sm ${
                    currentPage === p
                      ? "bg-blue-600 text-white"
                      : "bg-white hover:bg-slate-100"
                  }`}
                >
                  {p}
                </button>
              )
            );
          })()}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            className={`px-2 py-1 border rounded text-sm ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-slate-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}



// import { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import { BRAND } from "../css/brand";
// import { RefreshCw, RotateCcw } from "lucide-react";
// import BlueButton from "../Components/BlueButton";
// import GoBackButton from "../Components/GoBackButton";
// import TableSkeleton from "../Components/TableSkeleton";
// import { toast } from "react-toastify";
// import Swal from "sweetalert2";
// import "react-toastify/dist/ReactToastify.css";

// export default function DeletedCustomers() {
//   const [customers, setCustomers] = useState([]);
//   const [selectedIds, setSelectedIds] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(10); // ✅ dynamic rows per page

//   // ✅ Session data
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const loginId = authUser?.data?.[0]?.id || 26;

//   // ✅ Fetch deleted customers
//   const fetchDeletedCustomers = async (page = 1, limit = rowsPerPage) => {
//     setLoading(true);
//     setError(null);

//     try {
//       const res = await axios.get(
//         `https://spillas.com/api/v1/get-deleted-customers?page=${page}&per_page=${limit}&login_id=${loginId}&year=2025`
//       );
//       const data = res.data.data.data || [];
//       setCustomers(data);
//       setTotalPages(res.data.data.last_page || 1);
//       setSelectedIds([]);
//     } catch (err) {
//       console.error("Error fetching deleted customers:", err);
//       setError("Failed to load deleted customers.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchDeletedCustomers(currentPage, rowsPerPage);
//   }, [currentPage, rowsPerPage]); // ✅ refetch when limit changes

//   // ✅ Handle selection
//   const handleSelect = (id) => {
//     setSelectedIds((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   const handleSelectAll = () => {
//     if (selectedIds.length === customers.length) {
//       setSelectedIds([]);
//     } else {
//       setSelectedIds(customers.map((c) => c.id));
//     }
//   };

//   // ✅ Restore customers
//   const handleRestore = async () => {
//   if (selectedIds.length === 0) {
//     Swal.fire({
//       icon: "warning",
//       title: "No Customers Selected",
//       text: "Please select at least one customer to recover.",
//       confirmButtonColor: "#2563eb",
//     });
//     return;
//   }

//   const confirm = await Swal.fire({
//     title: "Recover selected customers?",
//     text: `You are about to recover ${selectedIds.length} customer(s).`,
//     icon: "question",
//     showCancelButton: true,
//     confirmButtonText: "Yes, Recover",
//     cancelButtonText: "Cancel",
//     confirmButtonColor: "#2563eb",
//   });

//   if (!confirm.isConfirmed) return;

//   try {
//     const res = await axios.post("https://spillas.com/api/v1/restore-customers", {
//       ids: selectedIds,
//       login_id: loginId,
//     });

//     if (res.data?.status === true) {
//       await Swal.fire({
//         icon: "success",
//         title: "Recovered!",
//         text: "Customers recovered successfully.",
//         confirmButtonColor: "#2563eb",
//       });
//       fetchDeletedCustomers(currentPage, rowsPerPage);
//     } else {
//       Swal.fire({
//         icon: "error",
//         title: "Failed",
//         text: "Failed to recover customers.",
//         confirmButtonColor: "#2563eb",
//       });
//     }
//   } catch (err) {
//     console.error("Error restoring customers:", err);
//     Swal.fire({
//       icon: "error",
//       title: "Error",
//       text: "An error occurred while recovering customers.",
//       confirmButtonColor: "#2563eb",
//     });
//   }
// };


//   return (
//     <div className="space-y-4">
//       <GoBackButton />
//       <Header brand={BRAND} title="Deleted Customers" />

//       {/* Top Actions */}
//       <div className="flex flex-wrap justify-end items-center gap-2 sm:gap-3">
//         {/* Rows per page (hidden on small screens) */}
//         <div className="hidden sm:flex items-center gap-2 text-sm">
//           <span>Rows per page:</span>
//           <select
//             value={rowsPerPage}
//             onChange={(e) => {
//               setRowsPerPage(Number(e.target.value));
//               setCurrentPage(1);
//             }}
//             className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//           >
//             {[10, 20, 50, 100].map((num) => (
//               <option key={num} value={num}>
//                 {num}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Compact dropdown for mobile */}
//         <div className="flex sm:hidden items-center">
//           <select
//             value={rowsPerPage}
//             onChange={(e) => {
//               setRowsPerPage(Number(e.target.value));
//               setCurrentPage(1);
//             }}
//             className="border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
//           >
//             {[10, 20, 50, 100].map((num) => (
//               <option key={num} value={num}>
//                 {num}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Buttons */}
//         <div className="flex gap-2">
//           {/* Refresh */}
//           <BlueButton
//             brand={BRAND}
//             onClick={() => fetchDeletedCustomers(currentPage, rowsPerPage)}
//             className="inline-flex items-center gap-1 sm:px-3 sm:py-2 px-2 py-1"
//           >
//             <RefreshCw size={16} />
//             <span className="hidden sm:inline">Refresh</span>
//           </BlueButton>

//           {/* Recover */}
//           <BlueButton
//             brand={BRAND}
//             onClick={handleRestore}
//             className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 sm:px-3 sm:py-2 px-2 py-1"
//           >
//             <RotateCcw size={16} />
//             <span className="hidden sm:inline">Recover</span>
//           </BlueButton>
//         </div>
//       </div>


//       {/* Table */}
//       {loading ? (
//         <TableSkeleton rows={6} cols={6} />
//       ) : error ? (
//         <p className="text-red-500">{error}</p>
//       ) : customers.length === 0 ? (
//         <p>No deleted customers found.</p>
//       ) : (
//         <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
//           <table className="min-w-full text-sm text-center">
//             <thead className="bg-slate-50 text-center">
//               <tr className="text-left text-[11px] sm:text-xs uppercase tracking-wide text-slate-500">
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50">
//                   <input
//                     type="checkbox"
//                     checked={
//                       customers.length > 0 &&
//                       selectedIds.length === customers.length
//                     }
//                     onChange={handleSelectAll}
//                   />
//                 </th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Sr No.</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Customer Name</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">IMEI</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Retailer Code</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Shop Name</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Registration</th>
//                 <th className="px-3 sm:px-2 py-2 sm:py-3 whitespace-nowrap bg-slate-50 text-center">Deleted</th>
//               </tr>
//             </thead>
//             <tbody>
//               {customers.map((item, index) => (
//                 <tr
//                   key={item.id}
//                   className={`border-t hover:bg-slate-50 ${
//                     selectedIds.includes(item.id) ? "bg-blue-50" : ""
//                   }`}
//                 >
//                   <td className="p-2 text-center">
//                     <input
//                       type="checkbox"
//                       checked={selectedIds.includes(item.id)}
//                       onChange={() => handleSelect(item.id)}
//                     />
//                   </td>
//                   <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
//                   <td>{item.name || "-"}</td>
//                   <td>{item.imei1 || "-"}</td>
//                   <td>{item.retailer_code || "-"}</td>
//                   <td>{item.created_by_name || "-"}</td>
//                   <td>
//                     {item.created_at
//                       ? new Date(item.created_at).toLocaleDateString()
//                       : "-"}
//                   </td>
//                   <td>
//                     {item.updated_at
//                       ? new Date(item.updated_at).toLocaleDateString()
//                       : "-"}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Pagination + Rows per page */}
//       <div className="flex flex-wrap justify-end items-center mt-4 gap-3">
//         {/* Rows per page selector */}
        

//         {/* Pagination */}
//         <div className="flex gap-2 items-center">
//           {/* Prev */}
//           <button
//             disabled={currentPage === 1}
//             onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//             className={`px-2 py-1 border rounded text-sm ${
//               currentPage === 1
//                 ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                 : "bg-white hover:bg-slate-100"
//             }`}
//           >
//             Prev
//           </button>

//           {/* Page numbers */}
//           {(() => {
//             const pages = [];
//             const maxVisible = 5;
//             let start = Math.max(1, currentPage - 2);
//             let end = Math.min(totalPages, start + maxVisible - 1);
//             if (end - start < maxVisible - 1)
//               start = Math.max(1, end - maxVisible + 1);
//             if (start > 1) {
//               pages.push(1);
//               if (start > 2) pages.push("...");
//             }
//             for (let i = start; i <= end; i++) pages.push(i);
//             if (end < totalPages) {
//               if (end < totalPages - 1) pages.push("...");
//               pages.push(totalPages);
//             }
//             return pages.map((p, idx) =>
//               p === "..." ? (
//                 <span key={idx} className="px-2 text-gray-500">
//                   ...
//                 </span>
//               ) : (
//                 <button
//                   key={p}
//                   onClick={() => setCurrentPage(p)}
//                   className={`px-2 py-1 border rounded text-sm ${
//                     currentPage === p
//                       ? "bg-blue-600 text-white"
//                       : "bg-white hover:bg-slate-100"
//                   }`}
//                 >
//                   {p}
//                 </button>
//               )
//             );
//           })()}

//           {/* Next */}
//           <button
//             disabled={currentPage === totalPages}
//             onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//             className={`px-2 py-1 border rounded text-sm ${
//               currentPage === totalPages
//                 ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                 : "bg-white hover:bg-slate-100"
//             }`}
//           >
//             Next
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }



// import { useState, useEffect } from "react";
// import axios from "axios";
// import Header from "../Components/Header";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";
// import { RefreshCw } from "lucide-react";
// import BlueButton from "../Components/BlueButton";
// import GoBackButton from "../Components/GoBackButton";
// import TableSkeleton from "../Components/TableSkeleton";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// export default function DeletedCustomers() {
//   const [customers, setCustomers] = useState([]);
//   console.log("customer", customers)
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [page, setPage] = useState(1);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const rowsPerPage = 10;

//   // ✅ Session data
//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const loginId = authUser?.data?.[0]?.id || 26; // fallback

//   const fetchDeletedCustomers = async (page = 1) => {
//     setLoading(true);
//     setError(null);

//     try {
//       const res = await axios.get(
//         `https://spillas.com/api/v1/get-deleted-customers?page=${page}&per_page=${rowsPerPage}&login_id=${loginId}&year=2025`
//       );

//       const data = res.data.data.data || [];
//       setCustomers(data);
//       setTotalPages(res.data.data.last_page || 1);

//       toast.success(`Loaded ${data.length} deleted customers`);
//     } catch (err) {
//       console.error("Error fetching deleted customers:", err);
//       setError("Failed to load deleted customers.");
//       toast.error("Failed to load deleted customers");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchDeletedCustomers(currentPage);
//   }, [currentPage]);

//   return (
//     <div className="space-y-4">
//       <GoBackButton />
//       <Header brand={BRAND} title="Deleted Customers" />

//       <div className="flex justify-end">
//         <BlueButton
//           brand={BRAND}
//           onClick={() => fetchDeletedCustomers(currentPage)}
//           className="inline-flex items-center gap-1"
//         >
//           <RefreshCw size={14} /> Refresh
//         </BlueButton>
//       </div>

//       {loading ? (
//         <TableSkeleton rows={6} cols={5} />
//       ) : error ? (
//         <p className="text-red-500">{error}</p>
//       ) : customers.length === 0 ? (
//         <p>No deleted customers found.</p>
//       ) : (
//         <SimpleTable
//           headers={["Sr No.","Customer Name", "IMEI", "Retailer Code", "Shop Name", "Registration", "Deleted"]}
//           rows={customers.map((item, index) => [
//             (page - 1) * rowsPerPage + index + 1,
//             item.name || "-",
//             item.imei1 || "-",
//             item.retailer_code || "-",
//             item.mobile || "-",
//             item.created_at ? new Date(item.created_at).toLocaleDateString() : "-",
//             item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-",
//             // item.deleted_by_name || "-",
//           ])}
//         />
//       )}

//       {/* Pagination */}
//       <div className="flex gap-2 justify-end mt-4 items-center">
//         {/* Prev Button */}
//         <button
//           disabled={currentPage === 1}
//           onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//           className={`px-2 py-1 border rounded text-sm ${
//             currentPage === 1
//               ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//               : "bg-white hover:bg-slate-100"
//           }`}
//         >
//           Prev
//         </button>

//         {/* Page numbers */}
//         {(() => {
//           const pages = [];
//           const maxVisible = 5;
//           let start = Math.max(1, currentPage - 2);
//           let end = Math.min(totalPages, start + maxVisible - 1);

//           if (end - start < maxVisible - 1) {
//             start = Math.max(1, end - maxVisible + 1);
//           }

//           if (start > 1) {
//             pages.push(1);
//             if (start > 2) pages.push("...");
//           }

//           for (let i = start; i <= end; i++) {
//             pages.push(i);
//           }

//           if (end < totalPages) {
//             if (end < totalPages - 1) pages.push("...");
//             pages.push(totalPages);
//           }

//           return pages.map((p, idx) =>
//             p === "..." ? (
//               <span key={idx} className="px-2 text-gray-500">
//                 ...
//               </span>
//             ) : (
//               <button
//                 key={p}
//                 onClick={() => setCurrentPage(p)}
//                 className={`px-2 py-1 border rounded text-sm ${
//                   currentPage === p
//                     ? "bg-blue-600 text-white"
//                     : "bg-white hover:bg-slate-100"
//                 }`}
//               >
//                 {p}
//               </button>
//             )
//           );
//         })()}

//         {/* Next Button */}
//         <button
//           disabled={currentPage === totalPages}
//           onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//           className={`px-2 py-1 border rounded text-sm ${
//             currentPage === totalPages
//               ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//               : "bg-white hover:bg-slate-100"
//           }`}
//         >
//           Next
//         </button>
//       </div>
//     </div>
//   );
// }
